import { supabase } from '../lib/supabase';
import { getRedirectUrl } from '../lib/deepLinks';

/**
 * Fetches users with coach names based on the current user's role and ID.
 * Uses the new database function that includes coach names in the results.
 * - Super admins can fetch all users.
 * - Coaches can fetch users assigned to them.
 * - Regular users can only fetch their own profile.
 *
 * @param {string} currentUserId - The ID of the current user.
 * @param {string} currentUserRole - The role of the current user ('SUPER_ADMIN', 'COACH', 'CLIENT').
 * @returns {Promise<Array>} A promise that resolves to an array of user profiles with coach names.
 */
export const getUsers = async (currentUserId, currentUserRole, options = {}) => {
  try {
    const {
      page = 1,
      pageSize = 10,
      search = '',
      roleFilter = '',
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;

    // Try using the optimized RPC function first
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_users_paginated', {
        current_user_id: currentUserId,
        current_user_role: currentUserRole,
        search_term: search,
        role_filter: roleFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        page_offset: (page - 1) * pageSize,
        page_limit: pageSize
      });

      if (!rpcError && rpcData && rpcData.length > 0) {
        const totalCount = rpcData[0]?.total_count || 0;
        return {
          data: rpcData,
          error: null,
          count: totalCount,
          page,
          pageSize,
          totalPages: Math.ceil(totalCount / pageSize)
        };
      }
    } catch (rpcErr) {
      console.warn('RPC function not available, using fallback:', rpcErr.message);
    }

    // Fallback to regular query with timeout protection
    console.warn('Using fallback query with pagination and search support');
    
    const queryTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout')), 10000) // 10 second timeout
    );

    let query = supabase
      .from('profiles')
      .select(`
        id,
        role,
        coach_id,
        full_name,
        first_name,
        last_name,
        email,
        avatar_url,
        created_at,
        updated_at,
        is_active
      `, { count: 'exact' });

    // Apply role-based filtering
    if (currentUserRole === 'COACH') {
      query = query.eq('coach_id', currentUserId);
    } else if (currentUserRole === 'CLIENT') {
      query = query.eq('id', currentUserId);
    }
    // For SUPER_ADMIN, no additional filter is needed

    // Apply search filter
    if (search.trim()) {
      query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,full_name.ilike.%${search}%,email.ilike.%${search}%`);
    }

    // Apply role filter
    if (roleFilter) {
      query = query.eq('role', roleFilter);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await Promise.race([query, queryTimeout]);

    if (error) {
      console.error('Error fetching users:', error.message);
      return { data: null, error: `Failed to fetch users: ${error.message}`, count: 0 };
    }

    // Format the data and fetch coach names with timeout protection
    let formattedData = data || [];
    
    // If we have users with coach_id, fetch coach names
    const uniqueCoachIds = [...new Set(formattedData
      .filter(user => user.coach_id)
      .map(user => user.coach_id))];
    
    let coachNames = {};
    if (uniqueCoachIds.length > 0) {
      try {
        const coachQuery = supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .in('id', uniqueCoachIds);
          
        const { data: coaches } = await Promise.race([coachQuery, queryTimeout]);
        
        coachNames = coaches?.reduce((acc, coach) => {
          acc[coach.id] = coach.first_name && coach.last_name
            ? `${coach.first_name} ${coach.last_name}`
            : coach.full_name || 'Unknown Coach';
          return acc;
        }, {}) || {};
      } catch (coachError) {
        console.warn('Failed to fetch coach names:', coachError.message);
      }
    }
    
    // Add coach names to the formatted data and ensure is_active defaults to true
    formattedData = formattedData.map(user => ({
      ...user,
      is_active: user.is_active !== undefined ? user.is_active : true, // Default to active if null
      coach_name: user.coach_id ? coachNames[user.coach_id] || 'Unknown Coach' : null
    }));

    return {
      data: formattedData,
      error: null,
      count,
      page,
      pageSize,
      totalPages: Math.ceil(count / pageSize)
    };
  } catch (err) {
    console.error('Error fetching users:', err.message);
    return { data: null, error: `Failed to fetch users: ${err.message}`, count: 0 };
  }
};

/**
 * Fetches all coaches for dropdown selection.
 * Uses fallback query if RPC function is not available.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of coach profiles.
 */
export const getCoaches = async () => {
  try {
    // Try using the new RPC function first
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_coaches');

    if (!rpcError && rpcData) {
      return { data: rpcData, error: null };
    }

    // Fallback to regular query if RPC fails
    console.warn('RPC function failed, using fallback query:', rpcError?.message);
    
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, full_name, email')
      .eq('role', 'COACH')
      .order('first_name, last_name');

    if (error) {
      console.error('Error fetching coaches:', error.message);
      return { data: null, error: `Failed to fetch coaches: ${error.message}` };
    }
    return { data, error: null };
  } catch (err) {
    console.error('Error fetching coaches:', err.message);
    return { data: null, error: `Failed to fetch coaches: ${err.message}` };
  }
};

/**
 * Adds a new user by signing them up with Supabase Auth.
 * The enhanced `handle_new_user` trigger will create the profile with metadata.
 *
 * @param {object} userData - The data for the new user, including email, password, and profile metadata.
 * @returns {Promise<object>} A promise that resolves to the newly created user object.
 */
export const addUser = async (userData) => {
  try {
    const { email, password, first_name, last_name, full_name, role, coach_id } = userData;

    // Prepare metadata for the trigger
    const metadata = {
      first_name,
      last_name,
      full_name,
      role: role || 'CLIENT',
      coach_id: coach_id || null
    };

    const {
      data: { user },
      error,
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata, // This data is passed to the enhanced `handle_new_user` trigger
      },
    });

    if (error) {
      console.error('Error signing up user:', error.message);
      throw new Error(`Failed to sign up user: ${error.message}`);
    }

    // The trigger handles profile creation with all metadata
    return user;
  } catch (err) {
    console.error('Error in addUser:', err.message);
    throw new Error(`Failed to create user: ${err.message}`);
  }
};

/**
 * Updates an existing user's data using the enhanced admin function.
 *
 * @param {string} userId - The ID of the user to update.
 * @param {object} updatedData - An object containing the fields to update.
 * @returns {Promise<object>} A promise that resolves to the updated user data.
 */
export const updateUser = async (userId, updatedData) => {
  try {
    const {
      email,
      role,
      first_name,
      last_name,
      full_name,
      coach_id,
      avatar_url
    } = updatedData;

    // Convert empty string coach_id to null for proper database handling
    const normalizedCoachId = coach_id === '' ? null : coach_id;

    // Try using the comprehensive admin function first
    try {
      const { error: rpcError } = await supabase.rpc('admin_update_user', {
        target_user_id: userId,
        user_email: email,
        user_role: role,
        user_first_name: first_name,
        user_last_name: last_name,
        user_full_name: full_name,
        user_coach_id: normalizedCoachId,
        user_avatar_url: avatar_url
      });

      if (!rpcError) {
        // Success with RPC function - refetch and return
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (!fetchError && data) {
          return data;
        }
      }
    } catch (rpcErr) {
      console.warn('Comprehensive admin function not available:', rpcErr.message);
    }

    // Special handling: If we're trying to unassign a coach (set to null),
    // bypass RPC functions and use direct update since the RPC logic is flawed
    if (normalizedCoachId === null && coach_id !== undefined) {
      const { error: directUpdateError } = await supabase
        .from('profiles')
        .update({
          coach_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (directUpdateError) {
        console.error('Direct coach unassignment failed:', directUpdateError.message);
        throw new Error(`Failed to unassign coach: ${directUpdateError.message}`);
      }

      // If other fields need updating, continue with fallback strategy
      if (email !== undefined || role !== undefined || first_name !== undefined ||
          last_name !== undefined || full_name !== undefined || avatar_url !== undefined) {
        // Continue with other field updates after coach unassignment
      } else {
        // Only coach was being updated, we can return early
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (fetchError) {
          console.error('Error refetching user profile:', fetchError.message);
          throw new Error(`Failed to refetch user profile: ${fetchError.message}`);
        }

        return data;
      }
    }

    // Fallback strategy: Use individual admin functions and update_profile
    console.warn('Using fallback update strategy with individual functions');
    
    let updateSuccess = false;

    // 1. Update basic profile fields using the update_profile function
    if (first_name !== undefined || last_name !== undefined || full_name !== undefined || avatar_url !== undefined) {
      try {
        const { error: profileError } = await supabase.rpc('update_profile', {
          user_id: userId,
          full_name: full_name,
          first_name: first_name,
          last_name: last_name,
          avatar_url: avatar_url
        });

        if (!profileError) {
          updateSuccess = true;
        } else {
          console.warn('update_profile failed:', profileError.message);
        }
      } catch (profileErr) {
        console.warn('update_profile function not available:', profileErr.message);
      }
    }

    // 2. Update role using admin_set_role
    if (role !== undefined) {
      try {
        const { error: roleError } = await supabase.rpc('admin_set_role', {
          target_user: userId,
          new_role: role
        });

        if (!roleError) {
          updateSuccess = true;
        } else {
          console.warn('Role update failed:', roleError.message);
        }
      } catch (roleErr) {
        console.warn('admin_set_role function not available:', roleErr.message);
      }
    }

    // 3. Update coach assignment using admin_set_coach
    if (coach_id !== undefined) {
      try {
        const { error: coachError } = await supabase.rpc('admin_set_coach', {
          target_user: userId,
          coach_user: normalizedCoachId
        });

        if (!coachError) {
          updateSuccess = true;
        } else {
          console.warn('Coach assignment failed:', coachError.message);
        }
      } catch (coachErr) {
        console.warn('admin_set_coach function not available:', coachErr.message);
      }
    }

    // 4. Try to update email if provided
    if (email !== undefined) {
      try {
        const { error: emailError } = await supabase.auth.admin.updateUserById(userId, {
          email: email
        });
        if (!emailError) {
          updateSuccess = true;
          // Also try to update email in profile
          try {
            await supabase
              .from('profiles')
              .update({ email: email })
              .eq('id', userId);
          } catch (emailProfileErr) {
            console.warn('Email profile update failed:', emailProfileErr.message);
          }
        } else {
          console.warn('Email update failed:', emailError.message);
        }
      } catch (emailErr) {
        console.warn('Email update not permitted:', emailErr.message);
      }
    }

    // If none of the above worked, try a direct profile update
    if (!updateSuccess) {
      const profileUpdates = {};
      if (first_name !== undefined) profileUpdates.first_name = first_name;
      if (last_name !== undefined) profileUpdates.last_name = last_name;
      if (full_name !== undefined) profileUpdates.full_name = full_name;
      if (avatar_url !== undefined) profileUpdates.avatar_url = avatar_url;
      if (email !== undefined) profileUpdates.email = email;
      if (coach_id !== undefined) profileUpdates.coach_id = normalizedCoachId;

      if (Object.keys(profileUpdates).length > 0) {
        const { error: directUpdateError } = await supabase
          .from('profiles')
          .update(profileUpdates)
          .eq('id', userId);

        if (directUpdateError) {
          console.error('Direct profile update failed:', directUpdateError.message);
          throw new Error(`Failed to update profile: ${directUpdateError.message}`);
        }
        updateSuccess = true;
      }
    }

    if (!updateSuccess) {
      throw new Error('No update operations were successful');
    }

    // Refetch the updated user data
    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error refetching user profile:', fetchError.message);
      throw new Error(`Failed to refetch user profile: ${fetchError.message}`);
    }

    return data;
  } catch (err) {
    console.error('Error in updateUser:', err.message);
    throw new Error(`Failed to update user: ${err.message}`);
  }
};

/**
 * Deletes a user using admin functions or fallback methods.
 *
 * @param {string} userId - The ID of the user to delete.
 * @returns {Promise<void>} A promise that resolves when the user is deleted.
 */
export const deleteUser = async (userId) => {
  try {
    // Try using the admin function first
    const { error: rpcError } = await supabase.rpc('admin_delete_user', {
      target_user_id: userId
    });

    if (!rpcError) {
      return { success: true };
    }

    // Fallback: Regular delete (will be subject to RLS)
    console.warn('RPC function failed, using fallback delete:', rpcError?.message);
    
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (error) {
      console.error('Error deleting user:', error.message);
      throw new Error(`Failed to delete user: ${error.message}`);
    }

    return { success: true };
  } catch (err) {
    console.error('Error in deleteUser:', err.message);
    throw new Error(`Failed to delete user: ${err.message}`);
  }
};

/**
 * Updates the password for the currently authenticated user.
 *
 * @param {string} newPassword - The new password for the user.
 * @returns {Promise<object>} A promise that resolves when the password has been updated.
 */
export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    console.error('Error updating password:', error.message);
    throw new Error(`Failed to update password: ${error.message}`);
  }
  return data;
};

/**
 * Sends a password reset email to a user (admin function).
 * This should only be called by Super Admins.
 *
 * @param {string} userId - The ID of the user to send password reset email to.
 * @returns {Promise<object>} A promise that resolves when the email is sent.
 */
export const adminSendPasswordResetEmail = async (userId) => {
  try {
    // Get user email
    const { data: user, error: userError } = await supabase
      .from('profiles')
      .select('email, first_name, last_name')
      .eq('id', userId)
      .single();
    
    if (userError) {
      throw new Error(`Could not find user: ${userError.message}`);
    }

    // Send password reset email using Supabase auth
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: getRedirectUrl('/reset-password')
    });

    if (error) {
      throw new Error(`Failed to send password reset email: ${error.message}`);
    }

    return {
      success: true,
      message: `Password reset email sent to ${user.first_name} ${user.last_name} (${user.email})`
    };
    
  } catch (err) {
    console.error('Error in adminSendPasswordResetEmail:', err.message);
    throw new Error(`Failed to send password reset email: ${err.message}`);
  }
};

/**
 * Generates a new password for any user (admin function).
 * This should only be called by Super Admins.
 * Since direct password updates require server-side functions, this generates a password
 * that can be sent to the user via email or other secure means.
 *
 * @param {string} userId - The ID of the user whose password to update.
 * @param {string} newPassword - The new password for the user (optional, will generate if not provided).
 * @returns {Promise<object>} A promise that resolves with the generated password.
 */
export const adminUpdateUserPassword = async (userId, newPassword = null) => {
  try {
    // Try using the database function first (if available)
    if (newPassword) {
      const { error: rpcError } = await supabase.rpc('admin_update_user_password', {
        target_user_id: userId,
        new_password: newPassword
      });
      
      if (!rpcError) {
        return { success: true, password: newPassword };
      }
      console.warn('Database function not available:', rpcError.message);
    }

    // Fallback: Generate a temporary password and provide instructions
    const generatedPassword = newPassword || Math.random().toString(36).slice(-12);
    
    // Get user info for context
    const { data: user, error: userError } = await supabase
      .from('profiles')
      .select('email, first_name, last_name')
      .eq('id', userId)
      .single();
    
    if (userError) {
      throw new Error(`Could not find user: ${userError.message}`);
    }

    // For now, return the generated password with instructions
    // In a production environment, you would:
    // 1. Use Supabase Auth Admin API (requires service role key)
    // 2. Send password reset email instead
    // 3. Use the database function (requires migration)
    
    return {
      success: true,
      password: generatedPassword,
      message: `Generated password for ${user.first_name} ${user.last_name} (${user.email}). Please send a password reset email or use Supabase Admin panel to update the password.`,
      instructions: 'Since direct password updates require server-side functions, please use the Supabase Admin panel or send a password reset email to the user.'
    };
    
  } catch (err) {
    console.error('Error in adminUpdateUserPassword:', err.message);
    throw new Error(`Failed to update user password: ${err.message}`);
  }
};

/**
 * Fetches a single user profile by their ID.
 *
 * @param {string} userId - The ID of the user to fetch.
 * @returns {Promise<{data: object, error: object}>} A promise that resolves to the user's profile data.
 */
export const fetchUserProfileById = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role')
    .eq('id', userId)
    .single();

  if (error && error.code !== 'PGRST116') {
    // PGRST116 means no rows found, which is not a critical error here.
    // The AuthContext will handle logging other errors.
    console.error('Error fetching user profile:', error);
  }
  
  // Ensure the function always returns a structured response.
  return { data, error: error || null };
};

/**
 * Gets total counts by role for the current user's access level.
 * - Super admins can see all users
 * - Coaches can see their assigned clients only
 *
 * @param {string} currentUserId - The ID of the current user
 * @param {string} currentUserRole - The role of the current user ('SUPER_ADMIN', 'COACH', 'CLIENT')
 * @param {object} options - Optional filters (search, roleFilter)
 * @returns {Promise<{coaches: number, clients: number, total: number}>}
 */
export const getRoleCounts = async (currentUserId, currentUserRole, options = {}) => {
  try {
    const { search = '', roleFilter = '' } = options;
    
    // Try using the optimized RPC function first
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_role_counts_fast', {
        current_user_id: currentUserId,
        current_user_role: currentUserRole,
        search_term: search,
        role_filter: roleFilter
      });

      if (!rpcError && rpcData && rpcData.length > 0) {
        const counts = rpcData[0];
        return {
          total: counts.total_count || 0,
          coaches: counts.coach_count || 0,
          clients: counts.client_count || 0,
          superAdmins: counts.super_admin_count || 0,
          error: null
        };
      }
    } catch (rpcErr) {
      console.warn('Optimized role counts RPC function not available, using fallback:', rpcErr.message);
    }

    // Fallback with timeout protection
    const queryTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout')), 8000) // 8 second timeout
    );
    
    let baseQuery = supabase.from('profiles').select('role', { count: 'exact', head: true });
    
    // Apply role-based filtering (same as getUsers function)
    if (currentUserRole === 'COACH') {
      baseQuery = baseQuery.eq('coach_id', currentUserId);
    } else if (currentUserRole === 'CLIENT') {
      baseQuery = baseQuery.eq('id', currentUserId);
    }
    // For SUPER_ADMIN, no additional filter is needed
    
    // Apply search filter if provided (same as getUsers function)
    if (search.trim()) {
      baseQuery = baseQuery.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,full_name.ilike.%${search}%,email.ilike.%${search}%`);
    }
    
    // Get total count with timeout
    const { count: totalCount, error: totalError } = await Promise.race([baseQuery, queryTimeout]);
    if (totalError) throw totalError;
    
    // Get coaches count with timeout
    let coachQuery = supabase.from('profiles').select('role', { count: 'exact', head: true }).eq('role', 'COACH');
    if (currentUserRole === 'COACH') {
      coachQuery = coachQuery.eq('coach_id', currentUserId);
    } else if (currentUserRole === 'CLIENT') {
      coachQuery = coachQuery.eq('id', currentUserId);
    }
    if (search.trim()) {
      coachQuery = coachQuery.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,full_name.ilike.%${search}%,email.ilike.%${search}%`);
    }
    
    const { count: coachCount, error: coachError } = await Promise.race([coachQuery, queryTimeout]);
    if (coachError) throw coachError;
    
    // Get clients count with timeout
    let clientQuery = supabase.from('profiles').select('role', { count: 'exact', head: true }).eq('role', 'CLIENT');
    if (currentUserRole === 'COACH') {
      clientQuery = clientQuery.eq('coach_id', currentUserId);
    } else if (currentUserRole === 'CLIENT') {
      clientQuery = clientQuery.eq('id', currentUserId);
    }
    if (search.trim()) {
      clientQuery = clientQuery.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,full_name.ilike.%${search}%,email.ilike.%${search}%`);
    }
    
    const { count: clientCount, error: clientError } = await Promise.race([clientQuery, queryTimeout]);
    if (clientError) throw clientError;
    
    return {
      total: totalCount || 0,
      coaches: coachCount || 0,
      clients: clientCount || 0,
      error: null
    };
  } catch (err) {
    console.error('Error getting role counts:', err.message);
    return {
      total: 0,
      coaches: 0,
      clients: 0,
      error: err.message
    };
  }
};

/**
 * Activates or deactivates a user account.
 * Only Super Admins and Coaches (for their clients) can perform this operation.
 *
 * @param {string} userId - The ID of the user to activate/deactivate.
 * @param {boolean} isActive - True to activate, false to deactivate.
 * @returns {Promise<object>} A promise that resolves to the updated user data.
 */
export const activateDeactivateUser = async (userId, isActive) => {
  try {
    const { data, error } = await supabase.rpc('activate_deactivate_user', {
      target_user_id: userId,
      new_active_status: isActive
    });

    if (error) {
      console.error('Error activating/deactivating user:', error.message);
      throw new Error(`Failed to ${isActive ? 'activate' : 'deactivate'} user: ${error.message}`);
    }

    return {
      success: true,
      data: data,
      message: `User ${isActive ? 'activated' : 'deactivated'} successfully`
    };
  } catch (err) {
    console.error('Error in activateDeactivateUser:', err.message);
    throw new Error(`Failed to ${isActive ? 'activate' : 'deactivate'} user: ${err.message}`);
  }
};

/**
 * Checks if a user account is active.
 *
 * @param {string} userId - The ID of the user to check.
 * @returns {Promise<boolean>} A promise that resolves to the user's active status.
 */
export const checkUserActiveStatus = async (userId) => {
  try {
    const { data, error } = await supabase.rpc('is_user_active', {
      user_id: userId
    });

    if (error) {
      console.error('Error checking user active status:', error.message);
      return false; // Default to inactive on error for security
    }

    return data === true;
  } catch (err) {
    console.error('Error in checkUserActiveStatus:', err.message);
    return false; // Default to inactive on error for security
  }
};